'use client';

import { useAuthToken } from '@/hooks/useAuthToken';
import { useI18n } from '@/contexts/I18nContext';
import { useEditorStore } from '@/lib/editor/editorStore';
import { AudioPanel } from './AudioPanel';
import { CaptionsPanel } from './panels/CaptionsPanel';
import { EffectsPanel } from './panels/EffectsPanel';
import { FiltersPanel } from './panels/FiltersPanel';
import { MediaPanel } from './panels/MediaPanel';
import { StickersPanel } from './panels/StickersPanel';
import { TemplatesPanel } from './panels/TemplatesPanel';
import { TextPanel } from './panels/TextPanel';
import { TransitionsPanel } from './panels/TransitionsPanel';

/**
 * Left tool-panel dispatcher (editor V2 rewrite, task 8).
 *
 * Reads the active `ToolId` straight from the store and renders the
 * matching panel — mirrors the monolith's `activeTool === '...'` switch in
 * `VideoEditor.tsx` (~line 1792+), but as one panel component per tool
 * instead of an inline ternary chain. Self-contained (no props): the shell
 * (task 9) just renders `<EditorLeftPanel />` inside its left `<aside>`.
 *
 * 'audio' reuses the pre-existing `AudioPanel` (built earlier, still on the
 * light theme) rather than a new panel — audio wasn't in this task's scope.
 */
export function EditorLeftPanel() {
  const activeTool = useEditorStore((s) => s.activeTool);
  const audioTrack = useEditorStore((s) => s.audioTrack);
  const audioTracks = useEditorStore((s) => s.audioTracks);
  const addAudioTrack = useEditorStore((s) => s.addAudioTrack);
  const selectAudioTrack = useEditorStore((s) => s.selectAudioTrack);
  const removeAudioTrack = useEditorStore((s) => s.removeAudioTrack);
  const { authedFetch } = useAuthToken();
  const { t } = useI18n();

  switch (activeTool) {
    case 'media':
      return <MediaPanel />;
    case 'text':
      return <TextPanel />;
    case 'stickers':
      return <StickersPanel />;
    case 'effects':
      return <EffectsPanel />;
    case 'audio':
      return (
        <AudioPanel
          authedFetch={authedFetch as unknown as typeof fetch}
          audioTrack={audioTrack}
          audioTracks={audioTracks}
          onAdd={addAudioTrack}
          onSelect={selectAudioTrack}
          onRemove={removeAudioTrack}
          t={t}
        />
      );
    case 'transitions':
      return <TransitionsPanel />;
    case 'captions':
      return <CaptionsPanel />;
    case 'filters':
      return <FiltersPanel />;
    case 'templates':
      return <TemplatesPanel />;
    default:
      return <MediaPanel />;
  }
}
